import React from "react"
import { FloatingActionButton, CircularProgress } from "material-ui"
import ContentAdd from "material-ui/svg-icons/content/add"
import AccountUsersTable from "./AccountUsersTable"
import AddAccountUserDialog from "./AddAccountUserDialog"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import AccountStore from "../../../stores/AccountStore"
import AppStateStore from "../../../stores/AppStateStore"
import TableActionBar from "../../../components/layout/TableActionBar"
import strings from "../../../localization/strings"
import autobind from "class-autobind"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import * as accountActions from "../../../actions/AccountActions"
import * as userActions from "../../../actions/UserActions"
import * as permissionHelper from "../../../helpers/permissionHelper"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import { IUserDocument, IHierarchyItem } from "../../../models"
import _ from "lodash"
import * as api from "../../../api"
import DeleteEssAccountUserConfirmDialog from "./DeleteEssAccountUserConfirmDialog"
import AccountUsersReport from "./AccountUsersReport"

interface IState {
	loading: boolean
	allUsers: IUserDocument[]
	users: IUserDocument[]
	selectedUsers: IUserDocument[]
	searchText: string
	openAddAccountUserDialog: boolean
	editUser: IUserDocument
	offset: number
	limit: number
	limitOptions: number[]
	showTableActionBar: boolean
	openDeleteEssAccountUserConfirmDialog: boolean
}

export default class AccountUsersTab extends React.Component<null, IState> {
	constructor(props) {
		super(props)
		this.state = {
			loading: true,
			allUsers: [],
			users: [],
			selectedUsers: [],
			searchText: "",
			openAddAccountUserDialog: false,
			editUser: null,
			offset: 0,
			limit: 50,
			limitOptions: [50, 100, 200],
			showTableActionBar: true,
			openDeleteEssAccountUserConfirmDialog: false,
		}
		autobind(this)
	}

	componentDidMount() {
		AccountStore.on("change", this.changeEvent)
		HierarchySettingsStore.on("change", this.changeEvent)
	}

	componentWillUnmount() {
		AccountStore.removeListener("change", this.changeEvent)
		HierarchySettingsStore.removeListener("change", this.changeEvent)
	}

	changeEvent() {
		this.setState({ offset: 0, selectedUsers: [] }, () => this.getUsers())
	}

	tableActionBarReload() {
		this.setState({ showTableActionBar: false }, () => this.setState({ showTableActionBar: true }))
	}

	getUsers() {
		const allUsers = AccountStore.getUsers().filter(user => {
			if (!user.Email) user.Email = ""
			const filterBySearchText =
				!this.state.searchText ||
				`${(user.FirstName ? user.FirstName : "").toLowerCase()} ${(user.LastName ? user.LastName : "").toLowerCase()}`.includes(this.state.searchText.toLowerCase()) ||
				(user.Email ? user.Email : "").toLowerCase().includes(this.state.searchText.toLowerCase())
			const filterByGlobalHierarchy =
				!HierarchySettingsStore.getGlobalLevelItemFilterId() ||
				(hierarchyHelper.getHierarchyItemAndChildrens(HierarchySettingsStore.getGlobalLevelItemFilterId()).find(item => item.Id === user.OrganizationHierarchyId) ? true : false)
			return filterBySearchText && filterByGlobalHierarchy
		})
		const users = allUsers ? allUsers.filter((v, index) => index >= this.state.offset && index < this.state.offset + this.state.limit) : []
		this.setState({ loading: false, allUsers, users })
	}

	onDeleteClick() {
		this.state.selectedUsers.find(user => permissionHelper.isAccountAdmin(user.UserRoleId)) ?
			AppStateStore.setProgressSnackbarContent(strings.Cannot_delete_Account_Admin_user) :
			this.state.selectedUsers.find(user => user.IsEmployee) ?
				this.setState({ openDeleteEssAccountUserConfirmDialog: true }) :
				AppStateStore.setConfirmDialogContent({
					title: this.state.selectedUsers.length > 1 ? strings.DELETE_SELECTED_USERS : strings.DELETE_SELECTED_USER,
					content: this.state.selectedUsers.length > 1 ? strings.Are_you_sure_you_want_to_delete_selected_Users : strings.Are_you_sure_you_want_to_delete_selected_User,
					confirm: () => this.onDeleteUser()
				})
	}

	onDeleteUser(togetherDeleteESS = false) {
		if (this.state.selectedUsers.find(user => user.Id === AuthenticationStore.getCookieUserId())) {
			AppStateStore.setProgressSnackbarContent(strings.You_cannot_delete_yourself)
		}
		else {
			AppStateStore.setProgressSnackbarContent("loading")
			Promise.all(this.state.selectedUsers.map(user => new Promise((resolve, reject) => {
				if (user.IsEmployee && !togetherDeleteESS) {
					user.AccountUser = false
					api.updateUser(user.Id, user)
						.then(res => resolve(res))
						.catch(err => reject(err))
				}
				else {
					api.deleteUser(user.Id)
						.then(res => resolve(res))
						.catch(err => reject(err))
				}
			})))
				.then(res => {
					AppStateStore.setProgressSnackbarContent(res.length > 1 ? strings.Account_Users_removed_successfully : strings.Account_User_removed_successfully)
					this.setState({ offset: 0, selectedUsers: [] })
					accountActions.getAccountUsers(AuthenticationStore.getCookieAccountId())
				})
				.catch(err => {
					AppStateStore.setProgressSnackbarContent(err.length > 1 ? strings.Failed_to_remove_Account_Users : strings.Failed_to_remove_Account_User)
					this.setState({ offset: 0, selectedUsers: [] })
					accountActions.getAccountUsers(AuthenticationStore.getCookieAccountId())
				})
		}
	}

	handleAdd(user: IUserDocument, closeDialog: boolean) {
		AppStateStore.setProgressSnackbarContent("loading")
		this.setState({ openAddAccountUserDialog: !closeDialog })
		return new Promise(resolve => {
			if (AccountStore.getExplicitAccountUserHierarchyAssignment() || AccountStore.getExplicitEmployeeHierarchyAssignment()) {
				if (user.OrganizationHierarchyId) {
					const chosenHierarchyItem: IHierarchyItem = _.cloneDeep(hierarchyHelper.getHierarchyItemById(user.OrganizationHierarchyId))
					if (AccountStore.getExplicitAccountUserHierarchyAssignment()) {
						const linkedUserIds = chosenHierarchyItem.LinkedUserIds ? chosenHierarchyItem.LinkedUserIds.slice() : []
						linkedUserIds.push(user.Id)
						chosenHierarchyItem.LinkedUserIds = linkedUserIds
					}
					if (AccountStore.getExplicitEmployeeHierarchyAssignment()) {
						const linkedEmployeeIds = chosenHierarchyItem.LinkedEmployeeIds ? chosenHierarchyItem.LinkedEmployeeIds.slice() : []
						linkedEmployeeIds.push(user.Id)
						chosenHierarchyItem.LinkedUserIds = linkedEmployeeIds
					}
					Promise.all([
						accountActions.addAccountUser(AccountStore.getCurrentAccount().Id, user),
						accountActions.updateAccountHierarchyItem(AuthenticationStore.getCookieAccountId(), chosenHierarchyItem, false)
					]).then(
						() => {
							resolve(true)
							AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
						},
						() => {
							resolve(false)
							AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
						}
					)
				} else {
					accountActions.addAccountUser(AccountStore.getCurrentAccount().Id, user).then(
						() => {
							resolve(true)
							AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
						},
						() => {
							resolve(false)
							AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
						}
					)
				}
			} else {
				accountActions.addAccountUser(AccountStore.getCurrentAccount().Id, user).then(
					() => {
						resolve(true)
						AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
					},
					() => {
						resolve(false)
						AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
					}
				)
			}
		})
	}

	handleUpdate(user: IUserDocument) {
		AppStateStore.setProgressSnackbarContent("loading")
		this.setState({ openAddAccountUserDialog: false })
		const chosenHierarchyItem: IHierarchyItem = _.cloneDeep(hierarchyHelper.getHierarchyItemById(user.OrganizationHierarchyId))
		if (chosenHierarchyItem &&
			(AccountStore.getExplicitAccountUserHierarchyAssignment() || AccountStore.getExplicitEmployeeHierarchyAssignment())
		) {
			const linkedUserIds = chosenHierarchyItem.LinkedUserIds ? chosenHierarchyItem.LinkedUserIds.slice() : []
			const linkedEmployeeIds = chosenHierarchyItem.LinkedEmployeeIds ? chosenHierarchyItem.LinkedEmployeeIds.slice() : []
			if ((AccountStore.getExplicitAccountUserHierarchyAssignment() && linkedUserIds.indexOf(user.Id) === -1) ||
				(AccountStore.getExplicitEmployeeHierarchyAssignment() && linkedEmployeeIds.indexOf(user.Id) === -1)
			) {
				if (AccountStore.getExplicitAccountUserHierarchyAssignment()) {
					linkedUserIds.push(user.Id)
					chosenHierarchyItem.LinkedUserIds = linkedUserIds
				}
				if (AccountStore.getExplicitEmployeeHierarchyAssignment()) {
					linkedEmployeeIds.push(user.Id)
					chosenHierarchyItem.LinkedEmployeeIds = linkedEmployeeIds
				}

				Promise.all([
					userActions.patchUser(user.Id, user),
					accountActions.updateAccountHierarchyItem(AuthenticationStore.getCookieAccountId(), chosenHierarchyItem, false)
				]).then(
					() => {
						accountActions.getAccountUsers(AccountStore.getCurrentAccount().Id)
						AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
					},
					() => {
						AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
					}
				)
			}
			else {
				userActions.patchUser(user.Id, user).then(
					() => {
						accountActions.getAccountUsers(AccountStore.getCurrentAccount().Id)
						AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
					},
					() => {
						AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
					}
				)
			}
		} else {
			userActions.patchUser(user.Id, user).then(
				() => {
					accountActions.getAccountUsers(AccountStore.getCurrentAccount().Id)
					AppStateStore.setProgressSnackbarContent(strings.User_saved_successfully)
				},
				() => {
					AppStateStore.setProgressSnackbarContent(strings.User_save_failed)
				}
			)
		}
	}

	render() {
		return (
			<div className="AccountUsersTab">
				<FloatingActionButton
					data-intercom-target={"menu-AccountUsers-Users-Btn-Add"}
					className="fixed-add"
					style={{ top: -28, right: 20, zIndex: 500 }}
					secondary
					onTouchTap={() => this.setState({ openAddAccountUserDialog: true, editUser: null })}
				>
					<ContentAdd />
				</FloatingActionButton>
				<div className="account-user-table-container" style={{ overflowY: "visible" }}>
					<div>
						{this.state.showTableActionBar &&
							<TableActionBar
								rowSelectionCount={this.state.selectedUsers.length}
								singleSelectedMessage={strings.Account_User}
								multipleSelectedMessage={strings.X_Account_Users}
								onSearch={searchText => this.setState({ searchText, offset: 0, selectedUsers: [] }, () => this.getUsers())}
								searchText={this.state.searchText}
								onDeleteClick={this.onDeleteClick}
								title={strings.Account_Users}
								customActionElement={
									<AccountUsersReport
										reportUsers={this.state.allUsers}
									/>
								}
							/>
						}
						{this.state.loading ?
							<div className="spinner">
								<CircularProgress />
							</div> :
							<AccountUsersTable
								users={this.state.users}
								onPageClick={offset => this.setState({ offset, selectedUsers: [] }, () => this.getUsers())}
								onLimitChange={limit => this.setState({ limit, offset: 0, selectedUsers: [] }, () => this.getUsers())}
								offset={this.state.offset}
								limit={this.state.limit}
								total={this.state.allUsers.length}
								limitOptions={this.state.limitOptions}
								handleSelectUser={(selectedUsers) => this.setState({ selectedUsers })}
								handleEditUser={editUser => this.setState({ editUser, openAddAccountUserDialog: true, selectedUsers: [] })}
							/>
						}
					</div>
				</div>
				{this.state.openAddAccountUserDialog &&
					<AddAccountUserDialog
						editUser={this.state.editUser}
						handleAdd={this.handleAdd}
						handleUpdate={this.handleUpdate}
						close={() => this.setState({ openAddAccountUserDialog: false })}
					/>
				}
				{this.state.openDeleteEssAccountUserConfirmDialog &&
					<DeleteEssAccountUserConfirmDialog
						users={this.state.selectedUsers}
						close={() => this.setState({ openDeleteEssAccountUserConfirmDialog: false })}
						confirm={togetherDeleteESS => {
							this.setState({ openDeleteEssAccountUserConfirmDialog: false })
							this.onDeleteUser(togetherDeleteESS)
						}}
					/>
				}
			</div>
		)
	}
}
